import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useBase } from '@airtable/blocks/interface/ui';
import { T } from '../../theme/tokens';
import { EASE, prefersReducedMotion } from '../../util/anim';
import DataGrid, { GridCtx, GridConfig, GridViewState, ColumnDef, KpiDef } from './DataGrid';
import { dockNamespace, getBlockIdBestEffort, migrateKey } from './dockViews';
import { exportProvenance } from '../../export/provenance';
import { buildCsv, withCsvMoney } from '../../export/csv';
import type { DockData, Parcel } from '../../data/useParcels';
import type { LensDef } from '../../data/lenses';
import { makePermitsConfig } from './configs/permits';
import { makeInspectionsConfig } from './configs/inspections';
import { makeSalesConfig } from './configs/sales';
import { makeUnitsConfig } from './configs/units';
import { makePunchConfig } from './configs/punch';
import { BASE_ID, TABLE } from '../../ids';
import { Btn } from '../../ui/primitives';
import { Menu } from '../../ui/Menu';

export type DockTab = 'Permits' | 'Inspections' | 'Sales' | 'Units' | 'Punch';
const TABS: DockTab[] = ['Permits', 'Inspections', 'Sales', 'Units', 'Punch'];
const fmt = (n: number) => n.toLocaleString('en-US');

/** Punch List submission FORM. The "+ Add punch item" button opens this intake form, prefilled
 *  with the selected unit (?prefill_Unit=…). If ever blanked, the button falls back to the table
 *  grid and shows a subtle "set form URL" note. Source: Punch List › form view share link. */
export const PUNCH_FORM_URL = 'https://airtable.com/apptHlTm4M43b35IA/pag7VMwL9IjPNzUYo/form';
const PUNCH_TABLE_URL = `https://airtable.com/${BASE_ID}/${TABLE.punch}`;

/** Map context for the dock's ↓PDF — lets it open with the same designed map page as the map
 *  toolbar's export (mini-map of the filtered lots, legend, filter-criteria provenance, and the
 *  split-by breakdown when layers are stacked). */
export interface DockMapExport {
  visible: Parcel[]; all: Parcel[]; lens: LensDef;
  criteria?: string; scope: string; totalSF: number;
  /** Active "Split by" lens stack — the map page's breakdown gains the level-1 split. */
  splits?: LensDef[];
}

function Dock({ data, visibleLotIds, ctx, open, height, onResize, onToggle, activeNoPermit, mapExport, uiScale = 1 }: {
  data: DockData & { loading: boolean };
  visibleLotIds: Set<string>;
  ctx: GridCtx;
  open: boolean; height: number;
  onResize: (h: number) => void; onToggle: () => void; activeNoPermit?: number;
  mapExport?: DockMapExport;
  /** Auto-density chrome zoom applied to the dock's wrapper: `height` stays in LOGICAL px
   *  (visual = height × uiScale), so viewport-px pointer deltas / window.innerHeight below
   *  are divided by uiScale before mixing with it. */
  uiScale?: number;
}) {
  // All dock persistence (last tab, saved views) lives under a key namespace scoped to this
  // base + block installation — two bases sharing the origin can't read each other's state.
  // One-time migration moves the audit-flagged unnamespaced legacy key ('lfe.dockTab') over.
  const base = useBase();
  const ns = useMemo(() => {
    const n = dockNamespace(base.id, getBlockIdBestEffort());
    try { migrateKey(localStorage, 'lfe.dockTab', `${n}.tab`); } catch { /* sandboxed */ }
    return n;
  }, [base.id]);
  // Remember the operator's last tab across sessions (sandbox-safe localStorage).
  const [tab, setTab] = useState<DockTab>(() => {
    try { const v = localStorage.getItem(`${ns}.tab`); return (TABS as string[]).includes(v || '') ? v as DockTab : 'Permits'; } catch { return 'Permits'; }
  });
  useEffect(() => { try { localStorage.setItem(`${ns}.tab`, tab); } catch { /* private mode */ } }, [tab, ns]);
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');
  const [inspPermitFilter, setInspPermitFilter] = useState<string | null>(null);
  const [safeOnly, setSafeOnly] = useState(false);
  const suppressClickRef = useRef(false); // set when a resize drag occurred, so the trailing click doesn't toggle
  const [dragging, setDragging] = useState(false);
  const resizeRafRef = useRef<number | null>(null);
  const resizePendingRef = useRef(0);

  // A child (permit/inspection) can link MULTIPLE lots; it's in scope if ANY of them is visible —
  // checking only lotIds[0] (`lotId`) dropped a row whose first lot was filtered out but a later
  // linked lot is still on the map.
  const inScope = <R extends { lotId: string | null; lotIds: string[] }>(rows: R[]) =>
    rows.filter(r => (r.lotId && visibleLotIds.has(r.lotId)) || (r.lotIds && r.lotIds.some(id => visibleLotIds.has(id))));
  // "Open permits" = Active Operating Permit (excludes Closed/Completed) — the real open universe (136),
  // not the narrow Interface-Safe flag (32). (Punch list P0-2.)
  const scopedPermits = useMemo(() => inScope(data.permitRows).filter(p => !safeOnly || p.activeOperating),
    [data.permitRows, visibleLotIds, safeOnly]);
  const scopedUnits = useMemo(() => inScope(data.unitRows), [data.unitRows, visibleLotIds]);
  const scopedInsps = useMemo(() => {
    let r = inScope(data.inspectionRows);
    if (inspPermitFilter) r = r.filter(x => x.permitNumber === inspPermitFilter);
    return r;
  }, [data.inspectionRows, visibleLotIds, inspPermitFilter]);
  const scopedSales = scopedUnits; // sales tab is unit-grain with the GHL join attached
  const scopedPunch = useMemo(() => inScope(data.punchRows), [data.punchRows, visibleLotIds]);

  const gridCtx: GridCtx = useMemo(() => ({
    ...ctx,
    jumpToInspections: (permitNumber: string) => { setInspPermitFilter(permitNumber); setTab('Inspections'); },
  }), [ctx]);

  // Inspections/Sales/Units configs are stable per tab. Only the Permits config folds in scoped
  // units + inspection counts, so isolate it — otherwise a filter edit (which changes scopedUnits)
  // rebuilt the column maps on every tab, not just Permits.
  const staticConfig = useMemo<GridConfig<any> | null>(() => {
    if (tab === 'Inspections') return makeInspectionsConfig();
    if (tab === 'Sales') return makeSalesConfig();
    if (tab === 'Units') return makeUnitsConfig();
    if (tab === 'Punch') return makePunchConfig();
    return null;
  }, [tab]);
  const permitsConfig = useMemo<GridConfig<any>>(
    () => makePermitsConfig({ scopedUnitRows: scopedUnits, inspCountByPermitNumber: data.inspCountByPermitNumber, activeNoPermit }),
    [scopedUnits, data.inspCountByPermitNumber, activeNoPermit]);
  const config: GridConfig<any> = tab === 'Permits' ? permitsConfig : staticConfig!;

  const rows: any[] = tab === 'Permits' ? scopedPermits : tab === 'Inspections' ? scopedInsps
    : tab === 'Sales' ? scopedSales : tab === 'Punch' ? scopedPunch : scopedUnits;
  const lotCount = visibleLotIds.size;

  // WYSIWYG exports: the grid further narrows `rows` (quick-search, time window, KPI quick-filter),
  // so it reports its visible set here and CSV/Excel/PDF export exactly what's on screen — INCLUDING
  // an empty view (a filter that matches nothing must export nothing, not silently fall back to the
  // full tab). null = grid hasn't reported for this tab yet (reset on tab switch so a stale set from
  // the previous tab can never leak into an export) → full tab scope.
  // The grid also reports its VIEW STATE (pre-quick-filter scope + active filters): the exports'
  // KPI band computes over that scope — the SAME numbers the on-screen strip shows — and a
  // provenance line spells out any narrowing so the band can never silently contradict the table.
  const viewRef = useRef<{ rows: any[]; view: GridViewState<any> } | null>(null);
  useEffect(() => { viewRef.current = null; }, [tab]);
  const handleViewChange = useCallback((r: any[], view: GridViewState<any>) => { viewRef.current = { rows: r, view }; }, []);
  const exportRows = (): any[] => viewRef.current ? viewRef.current.rows : rows;
  /** Full export view (computed at click time, like exportRows): KPI scope + provenance line +
   *  the WYSIWYG column set / curated KPI band / active grouping the grid is showing. */
  const exportView = (out: any[]): {
    kpiRows: any[]; kpiScopeRows: any[]; window: string; provenance: string;
    columns?: ColumnDef<any>[]; kpis?: KpiDef<any>[];
    group?: { label: string; keyOf: (r: any) => string };
    perspective: string | null;
  } => {
    const v = viewRef.current ? viewRef.current.view : null;
    // Excel rollup mirrors the OUTER (top) grouping level; nested sub-levels are an on-screen affordance.
    const groupOpt = v && v.groupBy && v.groupBy.length ? (config.groupOptions || []).find(o => o.key === v.groupBy[0]) : undefined;
    return {
      kpiRows: v ? v.scopedRows : rows,
      // pre-window scope + active window key → window-relative KPIs export their active-window
      // count (not the 48h placeholder), matching the on-screen strip.
      kpiScopeRows: v ? v.searchRows : rows,
      window: v ? v.window : 'all',
      provenance: exportProvenance({
        kpiLabels: v && v.kpiLabels, search: v && v.search, windowLabel: v && v.windowLabel,
        perspective: v && v.perspective,  // curated sub-view stamp, e.g. "Perspective: Intake & payments"
        group: v && v.groupLabels.length ? v.groupLabels.join(' › ') : null,
        visible: out.length, total: rows.length,
      }),
      columns: v ? v.exportColumns : undefined,
      kpis: v ? v.kpis : undefined,
      group: groupOpt ? { label: groupOpt.label, keyOf: groupOpt.keyOf } : undefined,
      perspective: v ? v.perspective : null,
    };
  };
  /** Self-describing filename: LFE-{tab}[-{perspective}]-{YYYY-MM-DD}.{ext} */
  const exportFilename = (ext: string): string => {
    const p = viewRef.current ? viewRef.current.view.perspective : null;
    const slug = (s: string) => s.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
    return `LFE-${tab}${p ? '-' + slug(p) : ''}-${new Date().toISOString().slice(0, 10)}.${ext}`;
  };

  // The centered grip: DRAG it (only when open) to resize the dock, CLICK it to toggle. The toggle
  // fires on the NATIVE click — reliable every time. The resize drag uses TEMPORARY WINDOW pointer
  // listeners (so moves keep arriving once the pointer leaves the small grip) and deliberately does
  // NOT use setPointerCapture — capture intermittently suppressed/retargeted the click and left the
  // button dead after a few open/close cycles. A real drag sets suppressClickRef so the trailing
  // click (if any) doesn't also toggle; the next pointerdown resets it.
  const onGripDown = useCallback((e: React.PointerEvent) => {
    suppressClickRef.current = false;           // fresh interaction
    if (!open) return;                          // closed: the click opens it — no drag machinery
    const startY = e.clientY, startH = height;
    const onMove = (ev: PointerEvent) => {
      const dy = (startY - ev.clientY) / uiScale; // viewport px → logical (chrome-zoomed) px
      if (Math.abs(dy) > 3) { suppressClickRef.current = true; setDragging(true); }
      const max = Math.round(window.innerHeight * 0.72 / uiScale); // cap = 72% of the VISUAL viewport
      // Coalesce to one state update per frame — pointermove can fire >100×/s.
      resizePendingRef.current = Math.max(220, Math.min(max, Math.round(startH + dy)));
      if (resizeRafRef.current != null) return;
      resizeRafRef.current = requestAnimationFrame(() => { resizeRafRef.current = null; onResize(resizePendingRef.current); });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      setDragging(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, [open, height, uiScale, onResize]);
  // The grip CLOSES the dock when open. OPENING is handled by the whole collapsed bar (onBarClick
  // below) so the target is the full 40px bar, not the tiny pill. A click right after a real
  // resize-drag is suppressed (the next pointerdown resets the flag).
  const onGripClick = useCallback(() => {
    if (!open) return;                 // closed → the whole bar opens it; the grip only closes
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    onToggle();
  }, [open, onToggle]);
  // The ENTIRE collapsed bar is the open target (the pill was too small to hit reliably). Only opens;
  // when already open this is a no-op so clicking tabs/controls/empty space never collapses the dock.
  const onBarClick = useCallback(() => { if (!open) onToggle(); }, [open, onToggle]);

  // "+ Add punch item" → opens the submission FORM (SDK is read-only — creation flows through an
  // Airtable Form, never the dock). When a lot is selected, prefill the Unit via ?prefill_Unit=<unit
  // name> (Airtable form prefill). With PUNCH_FORM_URL unset, fall back to the table grid + a note.
  const [punchMsg, setPunchMsg] = useState<string | null>(null);
  const selectedUnitName = useMemo(() => {
    const lotId = ctx.selectedLotId; if (!lotId) return '';
    // The unit a punch item attaches to — the unit linked to the selected lot (first match).
    const u = data.unitRows.find(r => r.lotId === lotId || r.lotIds.includes(lotId));
    return u ? u.unitName : '';
  }, [ctx.selectedLotId, data.unitRows]);
  const addPunchItem = useCallback(() => {
    const prefill = selectedUnitName ? `?prefill_Unit=${encodeURIComponent(selectedUnitName)}` : '';
    if (PUNCH_FORM_URL) { ctx.openUrl(PUNCH_FORM_URL + prefill); return; }
    // No form URL yet → open the table so the operator can still add a row, and flag the gap.
    ctx.openUrl(PUNCH_TABLE_URL);
    setPunchMsg('set form URL'); setTimeout(() => setPunchMsg(null), 3200);
  }, [ctx, selectedUnitName]);

  const [csvMsg, setCsvMsg] = useState<string | null>(null);
  const downloadCsv = () => {
    // WYSIWYG: the rows AND columns the grid currently shows (search/window/KPI filters applied,
    // perspective-curated column set), with `# ` comment lines stamping what produced the file.
    const out = exportRows();
    if (!out.length) { setCsvMsg('0 rows in view'); setTimeout(() => setCsvMsg(null), 2600); return; }
    const { provenance, columns } = exportView(out);
    // money columns render as uniform 2-dp in the CSV (the styled XLSX keeps raw numbers)
    const cols0 = columns && columns.length ? columns : config.columns;
    const csvCols = config.excel?.money?.length ? withCsvMoney(cols0, config.excel.money) : cols0;
    const csv = buildCsv(csvCols, out, [
      `LFE Site Map — ${tab} · exported ${new Date().toLocaleDateString('en-US')} · ${fmt(lotCount)} lots in view · ${fmt(out.length)} rows`,
      provenance,
    ]);
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = exportFilename('csv'); a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    setCsvMsg(`✓ ${out.length.toLocaleString('en-US')} rows`);  // visible confirmation — silent downloads read as broken
    setTimeout(() => setCsvMsg(null), 2600);
  };

  // Styled Excel export of the rows the grid currently SHOWS (same visible set as CSV/PDF) —
  // executive-workbook look: KPI cards, group rollup, toned status cells, native filterable
  // table. ExcelJS loads from CDN inside the lazy-loaded module, so none of it weighs on the
  // dock bundle.
  const [xlsxBusy, setXlsxBusy] = useState<string | false>(false);
  const downloadXlsx = async () => {
    if (xlsxBusy) return;
    const out = exportRows();
    // An empty view exports nothing (WYSIWYG) — flash why instead of downloading a blank workbook.
    if (!out.length) { setXlsxBusy('0 rows'); setTimeout(() => setXlsxBusy(false), 2600); return; }
    setXlsxBusy('…');
    try {
      const { buildDockWorkbookBytes } = await import('../../export/dockWorkbook');
      const { kpiRows, kpiScopeRows, window, provenance, columns, kpis, group } = exportView(out);
      const bytes = await buildDockWorkbookBytes({ tab, rows: out, config, lotCount, date: new Date().toLocaleDateString('en-US'),
        kpiRows, kpiScopeRows, window, provenance, columns, kpis, group });
      const blob = new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = exportFilename('xlsx'); a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch (e) {
      console.error('dock xlsx export failed', e); // #81 surface failures, don't fail silently
      setXlsxBusy('Export failed'); setTimeout(() => setXlsxBusy(false), 3000); return;
    } finally { setXlsxBusy(p => p === 'Export failed' ? p : false); }
  };

  // Branded PDF: page 1 = the SAME designed map dashboard as the map toolbar's ⤓ PDF (mini-map
  // of the filtered lots, legend, filter provenance), then the dock report — KPIs + a data table
  // of the rows the grid currently SHOWS, lot numbers matching the map. Lazy-loaded so jsPDF
  // stays out of the dock bundle.
  const [pdfBusy, setPdfBusy] = useState<string | false>(false);
  // #75 export options (persisted per browser, namespaced like the dock's other prefs): which
  // leading pages to include + the distribution measure. Defaults reproduce the prior one-click PDF.
  const pdfPref = (k: string, d: string) => { try { return localStorage.getItem(`${ns}.pdf.${k}`) ?? d; } catch { return d; } };
  const savePdfPref = (k: string, v: string) => { try { localStorage.setItem(`${ns}.pdf.${k}`, v); } catch { /* private mode */ } };
  const [pdfInclMap, setPdfInclMap] = useState(() => pdfPref('map', '1') !== '0');
  const [pdfMeasure, setPdfMeasure] = useState<'lots' | 'units' | 'val' | 'gpd'>(() => (pdfPref('measure', 'lots') as 'lots' | 'units' | 'val' | 'gpd'));
  useEffect(() => { savePdfPref('map', pdfInclMap ? '1' : '0'); }, [pdfInclMap]);
  useEffect(() => { savePdfPref('measure', pdfMeasure); }, [pdfMeasure]);
  /** Build the dock PDF with the current options; #80 `preview` opens it in a new tab instead of
   *  downloading (falls back to download if the extension sandbox blocks the popup). */
  const downloadPdf = async (preview = false) => {
    if (pdfBusy) return;
    const out = exportRows();
    if (!out.length) { setPdfBusy('0 rows'); setTimeout(() => setPdfBusy(false), 2600); return; }
    setPdfBusy(preview ? 'Preview…' : '…');
    try {
      const { buildDockPdf } = await import('../../export/buildDockPdf');
      const { kpiRows, kpiScopeRows, window: windowKey, provenance, columns, kpis, group, perspective } = exportView(out);
      // Columns: a curated perspective prints what it shows (WYSIWYG); the uncurated view keeps
      // the tab's pdfColumns print pin (a print-optimized subset of the full column set).
      const bytes = buildDockPdf({ tab, rows: out, config, lotCount, date: new Date().toLocaleDateString('en-US'), map: mapExport,
        kpiRows, kpiScopeRows, window: windowKey, provenance, columns: perspective ? columns : undefined, kpis, group,
        include: { map: pdfInclMap }, measure: pdfMeasure });
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const win = preview ? globalThis.open(url, '_blank', 'noopener') : null;
      if (!preview || !win) { const a = document.createElement('a'); a.href = url; a.download = exportFilename('pdf'); a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (e) {
      // #81 surface failures to the user — silent console.error left the button looking idle.
      console.error('dock pdf export failed', e);
      setPdfBusy('Export failed'); setTimeout(() => setPdfBusy(false), 3000); return;
    } finally { setPdfBusy(p => p === 'Export failed' ? p : false); }
  };

  const dockH = open ? height : 40;

  return (
    <div style={{ flex: `0 0 ${dockH}px`, height: dockH, minHeight: dockH, borderTop: `1px solid ${T.line}`,
      background: T.paper, display: 'flex', flexDirection: 'column', position: 'relative',
      boxShadow: '0 -10px 28px -12px rgba(28,38,32,.28)', // lifts the dock off the map
      transition: (dragging || prefersReducedMotion()) ? 'none' : `flex-basis .28s ${EASE}, height .28s ${EASE}` }}>

      {/* tab bar (40px) — when COLLAPSED the whole bar opens the dock (big, reliable target); tabs and
          controls keep their own handlers, and onBarClick is a no-op while open so they never collapse it. */}
      <div onClick={onBarClick} style={{ height: 40, minHeight: 40, display: 'flex', alignItems: 'center', gap: 4, padding: '0 12px', borderBottom: open ? `1px solid ${T.line}` : 'none', position: 'relative', cursor: open ? 'default' : 'pointer' }}>
        {/* centered grip — DRAG to resize, CLICK to collapse/expand. Off the right edge so it
            doesn't collide with the Omni button in the corner. */}
        <button onPointerDown={onGripDown} onClick={onGripClick}
          title={open ? 'Drag to resize · click to collapse' : 'Click to expand'}
          style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 9, height: 22, padding: '0 14px',
            border: `1px solid ${dragging ? T.accent : T.line}`, borderRadius: 999, background: T.paper, cursor: open ? 'ns-resize' : 'pointer',
            color: T.inkSoft, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, zIndex: 21, touchAction: 'none' }}>
          <span style={{ width: 22, height: 3, borderRadius: 99, background: dragging ? T.accent : T.line, display: 'inline-block' }} />
          {open ? '▾' : '▴'}
        </button>
        {TABS.map(t => (
          <Btn key={t} variant="ghost" size="tab" on={open && tab === t} onClick={() => setTab(t)}>
            {t}
          </Btn>
        ))}
        <span className="eyebrow num" style={{ marginLeft: 8, color: T.inkSoft }}>{fmt(lotCount)} lots in view</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {open && tab === 'Permits' && (
            <Btn variant="ghost" on={safeOnly} onClick={() => setSafeOnly(s => !s)} title="Show only OPEN permits — Active Operating (excludes Closed/Completed)"
              style={safeOnly ? { background: T.accent, color: T.onAccent, borderColor: T.accent } : undefined}>
              {safeOnly ? '✓ Open permits' : 'Open permits'}</Btn>
          )}
          {open && tab === 'Punch' && (
            <Btn variant="ghost" onClick={addPunchItem}
              title={PUNCH_FORM_URL
                ? `Open the Submit Punch Item form${selectedUnitName ? ` (prefilled for ${selectedUnitName})` : ''}`
                : 'Opens the Punch List table — paste the form share URL into PUNCH_FORM_URL to open a clean intake form instead'}
              style={{ background: T.accent, color: T.onAccent, borderColor: T.accent }}>
              + Add punch item{punchMsg ? <span style={{ marginLeft: 6, opacity: .85, fontWeight: 500 }}>· {punchMsg}</span> : ''}</Btn>
          )}
          {open && (
            <Btn variant="ghost" onClick={() => setDensity(d => d === 'comfortable' ? 'compact' : 'comfortable')}
              title="Row density">{density === 'comfortable' ? '↕ Comfortable' : '↕ Compact'}</Btn>
          )}
          {open && <Menu
            ariaLabel="PDF export options"
            title="Branded PDF — map of the current view, Key Findings, then the visible rows (lot numbers match the map). Choose what to include and how the distribution is weighted."
            label={pdfBusy ? `${pdfBusy} PDF` : '↓ PDF ▾'}
            align="right" minWidth={216}
            active={!pdfInclMap || pdfMeasure !== 'lots'}
            sections={[
              { title: 'Include', items: [
                { key: 'map', label: 'Map + distribution page', checkbox: true, keepOpen: true, checked: pdfInclMap, onSelect: () => setPdfInclMap(v => !v) },
              ] },
              { title: 'Distribution weighted by', items: ([['lots', 'Lot count'], ['units', 'Units'], ['val', 'Value'], ['gpd', 'GPD']] as const).map(([m, lbl]) => (
                { key: m, label: lbl, keepOpen: true, checked: pdfMeasure === m, onSelect: () => setPdfMeasure(m) })) },
            ]}
            footer={close => (
              <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderTop: `1px solid ${T.line}` }}>
                <Btn variant="solid" onClick={() => { close(); downloadPdf(false); }} style={{ flex: 1, justifyContent: 'center' }}>Download</Btn>
                <Btn variant="ghost" onClick={() => { close(); downloadPdf(true); }} title="Open the PDF in a new tab instead of downloading">Preview</Btn>
              </div>
            )}
          />}
          {open && <Btn variant="ghost" onClick={downloadXlsx} title="Styled Excel export of the rows visible in the grid (search/window/KPI filters applied)">{xlsxBusy ? `${xlsxBusy} Excel` : '↓ Excel'}</Btn>}
          {/* Punch List is manually maintained — no automated sync pipeline, so skip the freshness widget. */}
          {open && tab !== 'Punch' && (() => {
            // Data freshness + force-sync. Sales tab → GHL sync; other tabs → iWorQ permit monitor.
            const isSales = tab === 'Sales';
            const t = data.syncedAt ? (isSales ? data.syncedAt.sales : data.syncedAt.permits) : null;
            const wf = isSales ? 'ghl-airtable-sales-sync.yml' : 'glades-permit-monitor.yml';
            const when = t ? new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
            return <>
              {when && <span className="num" title={`Most recent ${isSales ? 'GHL sales sync' : 'iWorQ permit monitor'} write in this data`}
                style={{ fontSize: 11, color: T.inkSoft, whiteSpace: 'nowrap' }}>synced {when}</span>}
              <Btn variant="ghost" title={`Run the ${isSales ? 'GHL sales sync' : 'permit monitor'} now (opens GitHub Actions — press Run workflow)`}
                onClick={() => window.open(`https://github.com/Eric-Willens/GladesPermitTracker/actions/workflows/${wf}`, '_blank', 'noopener')}>⟳ Sync ↗</Btn>
            </>;
          })()}
          {open && <Btn variant="ghost" onClick={downloadCsv} title="Export the rows visible in the grid to CSV">{csvMsg || '↓ CSV'}</Btn>}
        </div>
      </div>

      {/* grid body */}
      {open && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <DataGrid rows={rows} config={config} ctx={gridCtx} density={density} onEscape={onToggle} fadeKey={tab}
            loading={data.loading && rows.length === 0} onViewChange={handleViewChange} uiScale={uiScale} viewsNs={ns}
            scopeCaption={inspPermitFilter && tab === 'Inspections'
              ? <Btn variant="ghost" onClick={() => setInspPermitFilter(null)}
                  style={{ color: T.accent }}>Permit {inspPermitFilter} ✕</Btn>
              : undefined} />
        </div>
      )}
    </div>
  );
}

// ghost toolbar style moved to ui/primitives Btn (variant="ghost") — pixel-identical.

export default React.memo(Dock);
